import React, { useState, useRef } from 'react';
import { toPng } from 'html-to-image';
import EidCard from './EidCard';
import CardEditor from './CardEditor';
import Navbar from './Navbar';
import Footer from './Footer';
import './EcardBuilder.css';

export const CARDS = [
  { id: 1, src: `${process.env.PUBLIC_URL}/assets/1.jpeg`, label: '', namePos: { x: 50, y: 91 }, defaultColor: '#ffffff' },
  { id: 2, src: `${process.env.PUBLIC_URL}/assets/2.jpeg`, label: '', namePos: { x: 50, y: 78 }, defaultColor: '#ffffff' },
  { id: 3, src: `${process.env.PUBLIC_URL}/assets/3.jpeg`, label: '', namePos: { x: 50, y: 88 }, defaultColor: '#ffffff' },
  { id: 4, src: `${process.env.PUBLIC_URL}/assets/4.jpeg`, label: '', namePos: { x: 50, y: 82 }, defaultColor: '#1a7a3c' },
];

const initSettings = (user) =>
  Object.fromEntries(
    CARDS.map((c) => [
      c.id,
      { name: user?.name || '', fontFamily: 'Cairo', nameColor: c.defaultColor, fontSize: 28 },
    ])
  );

export default function EcardBuilder({ user, onLogout }) {
  const [cardSettings, setCardSettings] = useState(() => initSettings(user));
  const [editingCardId, setEditingCardId] = useState(null);
  const [sharingCard, setSharingCard] = useState(null); // { id, platform }
  const cardRefs = useRef({});

  /* ── Generate image blob for a card ── */
  const getBlob = async (cardId) => {
    const el = cardRefs.current[cardId];
    if (!el) return null;
    const dataUrl = await toPng(el, { pixelRatio: 3, cacheBust: true });
    const res = await fetch(dataUrl);
    return res.blob();
  };

  /* ── Share a card from the grid hover overlay ── */
  const shareCard = async (cardId, platform, e) => {
    if (e) e.stopPropagation();
    setSharingCard({ id: cardId, platform });
    try {
      const blob = await getBlob(cardId);
      if (!blob) return;
      const file = new File([blob], 'eid-card.png', { type: 'image/png' });
      const cardName = cardSettings[cardId]?.name || '';
      const txt = encodeURIComponent(`عيد الفطر السعيد تهانينا لـ ${cardName} #عيد_مبارك`);

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'عيد الفطر السعيد', text: `تهانينا لـ ${cardName}` });
        return;
      }
      // Desktop fallback: download then open platform
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'eid-card.png';
      a.click();
      URL.revokeObjectURL(blobUrl);
      setTimeout(() => {
        if (platform === 'whatsapp') window.open(`https://wa.me/?text=${txt}`, '_blank', 'noopener');
        else window.open(`https://twitter.com/intent/tweet?text=${txt}`, '_blank', 'noopener');
      }, 400);
    } catch (err) {
      if (err.name !== 'AbortError') console.error(err);
    } finally {
      setSharingCard(null);
    }
  };

  /* ── Editor page ── */
  if (editingCardId !== null) {
    const card = CARDS.find((c) => c.id === editingCardId);
    return (
      <CardEditor
        card={card}
        settings={cardSettings[editingCardId]}
        user={user}
        onLogout={onLogout}
        onSave={(newSettings) => {
          setCardSettings((prev) => ({ ...prev, [editingCardId]: newSettings }));
          setEditingCardId(null);
        }}
        onBack={() => setEditingCardId(null)}
      />
    );
  }

  /* ── Main grid ── */
  return (
    <div className="builder-page">
      <Navbar user={user} onLogout={onLogout} />

      <main className="builder-main">

        <div className="cards-grid">
          {CARDS.map((card) => {
            const s = cardSettings[card.id];
            const busyWa = sharingCard?.id === card.id && sharingCard?.platform === 'whatsapp';
            const busyTw = sharingCard?.id === card.id && sharingCard?.platform === 'twitter';
            const busy = busyWa || busyTw;

            return (
              <div key={card.id} className="card-wrap" onClick={() => setEditingCardId(card.id)}>
                <p className="card-label">{card.label}</p>
                <div className="card-inner">
                  <EidCard
                    ref={(el) => { cardRefs.current[card.id] = el; }}
                    card={card}
                    name={s.name}
                    nameColor={s.nameColor}
                    fontSize={s.fontSize}
                    fontFamily={s.fontFamily}
                    namePos={card.namePos}
                    noShadow={card.id === 4}
                  />

                  {/* Hover overlay — share buttons stop propagation, dark bg lets click through to editor */}
                  <div className="card-overlay">
                    <p className="overlay-edit-hint">اضغط للتعديل</p>
                    <div className="overlay-share-box" onClick={(e) => e.stopPropagation()}>
                      <p className="overlay-share-label">مشاركة البطاقة</p>
                      <div className="overlay-btns">
                        <button
                          className="ov-btn ov-wa"
                          onClick={(e) => shareCard(card.id, 'whatsapp', e)}
                          disabled={busy}
                        >
                          {busyWa ? '…' : 'WhatsApp'}
                        </button>
                        <button
                          className="ov-btn ov-tw"
                          onClick={(e) => shareCard(card.id, 'twitter', e)}
                          disabled={busy}
                        >
                          {busyTw ? '…' : 'X / Twitter'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>
      <Footer />
    </div>
  );
}
